import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { AlertTriangle, CheckCircle2, Loader2, PoundSterling, RefreshCw, Truck } from "lucide-react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/DataTable";
import { StatusPill } from "@/components/StatusPill";
import { StateFilterSelect } from "@/components/StateFilterSelect";
import { InlineDetailPanel } from "@/components/InlineDetailPanel";
import {
  adminApi,
  type OpsAlertRow,
  type PostalReservationRow,
  type UpdatePricingConfigInput,
} from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { queryTableProps } from "@/lib/data-table";
import { postalReservationTone } from "@/lib/status-tone";
import { buildReferralPricingUpdate, REFERRAL_PRICING_FIELDS, type ReferralPricingKey } from "@/lib/referrals";
import { formatLondon, isoUtc } from "@/lib/format-time";

export const Route = createFileRoute("/_authed/postal")({
  component: PostalPage,
});

function PostalPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Postal operations</h1>
        <p className="text-sm text-muted-foreground">
          Carrier (Parcel2Go) ops alerts and reservations. Audit-logged.
        </p>
      </div>

      <PricingCard />
      <OpsAlertsCard />
      <ReservationsCard />
    </div>
  );
}

// ── Postage pricing ──────────────────────────────────────────────────────────

/**
 * The PinStaQ handling fee added on top of every live Parcel2Go quote
 * (functions: postage-quote-service). Editable here; the functions runtime
 * caches config for five minutes, so a change reaches owners within that.
 */
function PricingCard() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [limitDraft, setLimitDraft] = useState<string | null>(null);
  const [referralDrafts, setReferralDrafts] = useState<Partial<Record<ReferralPricingKey, string>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: queryKeys.pricingConfig.all,
    queryFn: async () => (await adminApi.getPricingConfig()()).data,
    staleTime: LIST_STALE_MS,
  });
  const config = query.data;
  const currency = (config?.postalReturnCurrency ?? "gbp").toUpperCase();

  async function saveFee() {
    const fee = Number(draft);
    if (!Number.isFinite(fee) || fee < 0 || fee > 50) {
      setError("Enter a fee between 0.00 and 50.00");
      return;
    }
    if (!window.confirm(`Set the postal handling fee to £${fee.toFixed(2)}? Owners see it on their next quote.`)) return;
    await submit({ postalHandlingFee: fee }, () => setDraft(null));
  }

  async function saveLimit() {
    const limit = Number(limitDraft);
    if (!Number.isInteger(limit) || limit < 1 || limit > 200) {
      setError("Enter a whole number between 1 and 200");
      return;
    }
    if (!window.confirm(`Set the Premium digital tag limit to ${limit}? It applies to every Premium account.`)) return;
    await submit({ generatedTagLimit: limit }, () => setLimitDraft(null));
  }

  async function saveReferralField(key: ReferralPricingKey, label: string) {
    const built = buildReferralPricingUpdate(key, referralDrafts[key] ?? "");
    if (!built.ok) {
      setError(built.error);
      return;
    }
    if (!window.confirm(`Set "${label}" to ${referralDrafts[key]}? It applies to new referrals straight away.`)) return;
    await submit(built.input, () => setReferralDrafts(({ [key]: _gone, ...rest }) => rest));
  }

  async function submit(input: UpdatePricingConfigInput, onDone: () => void) {
    setBusy(true);
    setError(null);
    try {
      const res = await adminApi.updatePricingConfig()(input);
      queryClient.setQueryData(queryKeys.pricingConfig.all, res.data);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <PoundSterling className="h-4 w-4" /> Postage pricing
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={() => query.refetch()} disabled={query.isFetching}>
          <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {query.isLoading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        )}
        {query.isError && (
          <p className="text-sm text-destructive">Could not load the pricing configuration.</p>
        )}
        {config && (
          <>
            <p className="text-sm text-muted-foreground">
              Handling fee added on top of the live carrier quote. Owners pay carrier price + this fee.
              Changes apply within five minutes.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Handling fee ({currency})</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="50"
                  className="w-32"
                  value={draft ?? config.postalHandlingFee.toFixed(2)}
                  onChange={(e) => setDraft(e.target.value)}
                  disabled={busy}
                />
              </label>
              <Button size="sm" onClick={saveFee} disabled={busy || draft === null}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
              {draft !== null && (
                <Button size="sm" variant="ghost" onClick={() => { setDraft(null); setError(null); }} disabled={busy}>
                  Cancel
                </Button>
              )}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Premium digital tag limit</span>
                <Input
                  type="number"
                  step="1"
                  min="1"
                  max="200"
                  className="w-32"
                  value={limitDraft ?? String(config.generatedTagLimit)}
                  onChange={(e) => setLimitDraft(e.target.value)}
                  disabled={busy}
                />
              </label>
              <Button size="sm" onClick={saveLimit} disabled={busy || limitDraft === null}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
              {limitDraft !== null && (
                <Button size="sm" variant="ghost" onClick={() => { setLimitDraft(null); setError(null); }} disabled={busy}>
                  Cancel
                </Button>
              )}
            </div>
            <p className="pt-2 text-sm font-medium">Referrals</p>
            <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
              {REFERRAL_PRICING_FIELDS.map((f) => {
                const draft = referralDrafts[f.key];
                return (
                  <div key={f.key} className="flex items-end gap-2">
                    <label className="space-y-1 text-sm">
                      <span className="text-muted-foreground">{f.label}</span>
                      <Input
                        type="number"
                        step="1"
                        min={f.min}
                        max={f.max}
                        className="w-28"
                        value={draft ?? String(config[f.key])}
                        onChange={(e) => setReferralDrafts((d) => ({ ...d, [f.key]: e.target.value }))}
                        disabled={busy}
                      />
                    </label>
                    <Button size="sm" onClick={() => saveReferralField(f.key, f.label)} disabled={busy || draft === undefined}>
                      Save
                    </Button>
                  </div>
                );
              })}
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <p className="text-xs text-muted-foreground">
              {config.updatedAt ? (
                <>
                  Last changed{" "}
                  <span title={isoUtc(config.updatedAt)}>{formatLondon(config.updatedAt)}</span>
                  {config.updatedBy ? ` by ${config.updatedBy}` : ""}
                </>
              ) : (
                "Using the built-in default — never changed."
              )}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Ops alerts ───────────────────────────────────────────────────────────────

const ALERT_TYPE_LABEL: Record<string, string> = {
  carrier_order_cancel_needed: "Cancel P2G order (portal)",
  dropoff_uncharged: "Parcel scanned, owner NOT charged (do not cancel P2G order)",
  carrier_prepay_low: "PrePay balance low",
};

function OpsAlertsCard() {
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [statusFilter, setStatusFilter] = useState<"open" | "resolved" | "all">("open");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: queryKeys.opsAlerts.list({ status: statusFilter }),
    queryFn: async ({ pageParam }) => {
      const status = statusFilter === "all" ? undefined : statusFilter;
      const res = await adminApi.listOpsAlerts()({ ...(status ? { status } : {}), cursor: pageParam });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = query.data?.pages.flatMap((p) => p.alerts) ?? [];
  const openCount = query.data?.pages[0]?.openCount ?? 0;
  const totalCount = query.data?.pages[0]?.totalCount ?? 0;

  // A filter switch hides rows an operator selected: clear the selection so
  // a bulk resolve cannot act on rows that are no longer visible.
  useEffect(() => {
    setSelection({});
  }, [statusFilter]);

  const selectedIds = useMemo(
    () => Object.keys(selection).filter((k) => selection[k]),
    [selection],
  );
  // Only open alerts can be resolved (the CF skips the rest and reports them).
  const openSelected = selectedIds.filter((id) => !rows.find((a) => a.id === id)?.resolvedAt).length;

  async function resolveSelected() {
    if (openSelected === 0) return;
    const ok = window.confirm(
      `Mark ${selectedIds.length} alert(s) as resolved? Only do this after the ` +
        "underlying action is done (e.g. the P2G order is cancelled in the portal).",
    );
    if (!ok) return;
    setBusy(true);
    setBanner(null);
    try {
      const res = await adminApi.resolveOpsAlerts()({
        alertIds: selectedIds,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setBanner(`Resolved ${res.data.resolved}, skipped ${res.data.skipped}.`);
      setSelection({});
      setNote("");
      await queryClient.invalidateQueries({ queryKey: queryKeys.opsAlerts.all });
    } catch (e) {
      setBanner(`Failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const columns: ColumnDef<OpsAlertRow, any>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    },
    {
      header: "Type",
      accessorKey: "type",
      cell: ({ row }) => (
        <span className="font-medium">
          {ALERT_TYPE_LABEL[row.original.type] ?? row.original.type}
        </span>
      ),
    },
    {
      header: "Status",
      cell: ({ row }) =>
        row.original.resolvedAt ? (
          <StatusPill tone="success">resolved</StatusPill>
        ) : (
          <StatusPill tone="warning">open</StatusPill>
        ),
    },
    {
      header: "Created",
      cell: ({ row }) =>
        row.original.createdAt
          ? format(new Date(row.original.createdAt), "d MMM yyyy HH:mm")
          : "—",
    },
    {
      header: "Details",
      cell: ({ row }) => (
        <span
          className="block max-w-[360px] truncate font-mono text-[11px] text-muted-foreground"
          title={JSON.stringify(row.original.data, null, 2)}
        >
          {Object.entries(row.original.data)
            .map(([k, v]) => `${k}=${String(v)}`)
            .join(" · ") || "—"}
        </span>
      ),
    },
    {
      header: "Resolution",
      cell: ({ row }) =>
        row.original.resolvedAt ? (
          <span className="text-xs text-muted-foreground">
            {format(new Date(row.original.resolvedAt), "d MMM HH:mm")}
            {row.original.resolutionNote ? ` — ${row.original.resolutionNote}` : ""}
          </span>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Ops alerts
            <span className="text-sm font-normal text-muted-foreground">
              {openCount} open · {totalCount} total
            </span>
          </CardTitle>
          <div className="flex items-center gap-1">
            {(["open", "resolved", "all"] as const).map((f) => (
              <Button
                key={f}
                size="sm"
                variant={statusFilter === f ? "default" : "ghost"}
                onClick={() => setStatusFilter(f)}
              >
                {f}
              </Button>
            ))}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
            >
              <RefreshCw className={query.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {banner && (
          <div className="rounded-md border bg-muted px-3 py-2 text-sm">{banner}</div>
        )}

        {selectedIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-md border bg-card/50 p-3">
            <span className="text-sm">
              {selectedIds.length} selected
              {openSelected !== selectedIds.length && (
                <span className="text-muted-foreground">
                  {" "}
                  ({openSelected} open)
                </span>
              )}
            </span>
            <Input
              className="h-8 max-w-xs"
              placeholder="Resolution note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
            />
            <Button
              size="sm"
              onClick={() => void resolveSelected()}
              disabled={busy || openSelected === 0}
            >
              {busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Mark resolved
            </Button>
          </div>
        )}

        <DataTable
          data={rows}
          columns={columns}
          isFetchingMore={query.isFetchingNextPage}
          hasMore={Boolean(query.hasNextPage)}
          onLoadMore={() => void query.fetchNextPage()}
          emptyMessage={
            statusFilter === "open" ? "No open alerts — all clear." : "No alerts."
          }
          rowKey={(r) => r.id}
          rowSelection={selection}
          onRowSelectionChange={setSelection}
          {...queryTableProps(query)}
        />
      </CardContent>
    </Card>
  );
}

// ── Carrier reservations ─────────────────────────────────────────────────────

const RESERVATION_STATUSES = [
  "awaiting_dropoff",
  "dropped_off",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "expired",
] as const;

function ReservationsCard() {
  const [status, setStatus] = useState<string>("");
  const [detailId, setDetailId] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: queryKeys.postal.list({ status }),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.listPostalReservations()({
        ...(status ? { status } : {}),
        cursor: pageParam,
      });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = query.data?.pages.flatMap((p) => p.reservations) ?? [];
  const totalCount = query.data?.pages[0]?.totalCount ?? 0;

  const detail = useQuery({
    queryKey: queryKeys.postal.detail(detailId ?? "none"),
    queryFn: async () => {
      const res = await adminApi.getPostalReservation()({ id: detailId! });
      return res.data;
    },
    enabled: !!detailId,
  });

  const columns: ColumnDef<PostalReservationRow, any>[] = [
    {
      header: "Reservation",
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <div className="font-mono text-xs">{row.original.reservationId}</div>
          <div className="text-[11px] text-muted-foreground">
            return {row.original.returnId}
          </div>
        </div>
      ),
    },
    {
      header: "Status",
      cell: ({ row }) => (
        <StatusPill tone={postalReservationTone(row.original.status)}>
          {row.original.status}
        </StatusPill>
      ),
    },
    {
      header: "Network",
      cell: ({ row }) => row.original.dropOffNetwork ?? row.original.carrier ?? "—",
    },
    {
      header: "Tracking",
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.trackingNumber ?? "—"}</span>
      ),
    },
    {
      header: "QR",
      cell: ({ row }) =>
        row.original.qrPending ? (
          <StatusPill tone="warning">pending</StatusPill>
        ) : (
          <StatusPill tone="success">ready</StatusPill>
        ),
    },
    {
      header: "Reserved",
      cell: ({ row }) =>
        row.original.reservedAt
          ? format(new Date(row.original.reservedAt), "d MMM HH:mm")
          : "—",
    },
    {
      header: "Expires",
      cell: ({ row }) => {
        if (!row.original.expiresAt) return "—";
        const overdue =
          row.original.status === "awaiting_dropoff" &&
          row.original.expiresAt < Date.now();
        return (
          <span className={overdue ? "font-medium text-destructive" : undefined}>
            {format(new Date(row.original.expiresAt), "d MMM HH:mm")}
          </span>
        );
      },
    },
    {
      header: "Reminders",
      cell: ({ row }) => row.original.remindersSent,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Carrier reservations
            <span className="text-sm font-normal text-muted-foreground">
              {totalCount} total
            </span>
          </CardTitle>
          <StateFilterSelect value={status} onChange={setStatus} options={RESERVATION_STATUSES} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <DataTable
          data={rows}
          columns={columns}
          isFetchingMore={query.isFetchingNextPage}
          hasMore={Boolean(query.hasNextPage)}
          onLoadMore={() => void query.fetchNextPage()}
          onRowClick={(row) => setDetailId(row.id === detailId ? null : row.id)}
          emptyMessage="No carrier reservations yet."
          rowKey={(r) => r.id}
          footerNote="Click a row for the full reservation document."
          {...queryTableProps(query)}
        />

        {detailId && (
          <InlineDetailPanel
            title={`Reservation ${detailId}`}
            onClose={() => setDetailId(null)}
            isLoading={detail.isLoading}
            error={detail.error as Error | null}
          >
            <pre className="max-h-96 overflow-auto rounded-md border bg-muted p-3 text-xs">
              {JSON.stringify(detail.data?.reservation, null, 2)}
            </pre>
          </InlineDetailPanel>
        )}
      </CardContent>
    </Card>
  );
}
